import { useEffect, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import { track } from '../../utils/track';
import { useOfferSlug } from './giveawayOfferPages';
import GiveawayOfferSocialLinks, { DISCORD_URL } from './GiveawayOfferSocialLinks';
import {
  PAGE_BG,
  CYAN,
  GOLD,
  INK,
  BODY,
  MUTED,
  LINE,
  kickerSx,
  ctaSx,
  panelSx
} from './giveawayOfferTheme';

/* `/giveaway-offer/subscribed` — where a subscription bought from the QR funnel lands.
 *
 * WHY THIS IS NOT `/giveaway-subs/confirmation`. That page is shared by three
 * routes (`/giveaway/confirmation-one-off`, `/giveaway/confirmation-sub` and
 * `/giveaway-subs/confirmation`) and is styled for the light giveaway funnel.
 * Darkening it in place would restyle the live giveaway flow for everyone, so
 * this funnel gets its own screen instead and reads the SAME
 * `giveawayConfirmationState` contract.
 *
 * IT DELIBERATELY DOES NOT OFFER THE MEMBER PORTAL. Everyone landing here signed
 * up from a poster and then subscribed, which under the current rule grants their
 * entries WITHOUT the verification click — so they have no password yet, and a
 * "go to member portal" button would send them to a login they cannot complete.
 * The password card below points at the email they were sent at signup, which
 * links to `/set-password?code=…` (server `lib/email.js` sendVerificationEmail).
 *
 * NO AD PIXELS HERE, DELIBERATELY. The shared confirmation page fires Meta and
 * TikTok `Purchase` events; this funnel does not, by request. A poster scan is
 * not an ad click, so feeding these purchases into ad attribution would misreport
 * what the ads actually produced. First-party `track()` still records the view,
 * so the funnel is still measurable in our own analytics.
 *
 * If that is ever reversed, copy the pixel block from
 * RafflePaymentConfirmationPage rather than writing a new one — it carries a
 * `purchase_tracked:<id>` de-dupe key that stops a refresh double-counting.
 */

type PackItem = {
  id: string;
  title: string;
  entries?: number;
  nfts?: number;
  tokens?: number;
};

type ConfirmationState = {
  entry?: { firstName?: string; email?: string };
  selectedPack?: PackItem;
  totalEntries?: number;
  nftRewards?: number;
  amountPaidAud?: number;
  incPerMonth?: number;
  checkoutMode?: 'standalone' | 'subscription';
  paymentIntentId?: string;
  subscriptionId?: string;
  upsellApplied?: unknown;
};

const nextSteps = [
  {
    Icon: EmailOutlinedIcon,
    title: 'Create your password',
    copy: 'Use the link in the email we sent when you signed up. It is the only way into your account.'
  },
  {
    Icon: CalendarMonthOutlinedIcon,
    title: 'Watch for the draw date',
    /* The DATE is announced in Discord; the draw itself is watched on Facebook. */
    copy: 'Announced on our website and in Discord. Draws run live on Facebook every Friday at 10pm AEST.'
  },
  {
    Icon: EmojiEventsOutlinedIcon,
    title: 'Winners announced',
    copy: 'Winners are notified by email and announced publicly after the live draw.'
  }
];

const GiveawayOfferSubscribedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  /* The path the funnel was opened on — "back to the offer" returns there. */
  const offerSlug = useOfferSlug();

  const state = useMemo<ConfirmationState | null>(() => {
    const fromRouter = location.state as ConfirmationState | undefined;
    if (fromRouter) return fromRouter;

    try {
      const saved = sessionStorage.getItem('giveawayConfirmationState');
      return saved ? (JSON.parse(saved) as ConfirmationState) : null;
    } catch {
      return null;
    }
  }, [location.state]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Clear the in-flight checkout state, exactly as the shared confirmation page
   * does. The confirmation state itself is left alone so a refresh still renders. */
  useEffect(() => {
    try {
      sessionStorage.removeItem('rafflePaymentPayload');
      sessionStorage.removeItem('giveawayUpsellPaymentIntentId');
      sessionStorage.removeItem('giveawayUpsellApplied');
    } catch {
      /* storage unavailable — nothing to clean up */
    }
    track('giveaway_offer.subscribed_view', { offerSlug });
  }, [offerSlug]);

  const shell = (children: ReactNode) => (
    <Box
      sx={{
        background: PAGE_BG,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SiteHeader />
      {children}
      <RaffleFooter />
    </Box>
  );

  /* A hard refresh with no state left. Same situation the shared page handles,
   * answered in this funnel's own colours and pointed back at this funnel. */
  if (!state) {
    return shell(
      <Box
        sx={{
          maxWidth: 640,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Typography sx={{ ...panelSx, p: 4, color: INK, fontSize: 20, fontWeight: 800 }}>
          We could not find your confirmation details.
        </Typography>
        <Button onClick={() => navigate(`/${offerSlug}`)} sx={{ ...ctaSx, mt: 3 }}>
          Back to the offer
        </Button>
      </Box>
    );
  }

  const isSubscription = state.checkoutMode === 'subscription';
  const rows: Array<[string, string]> = [
    ['Email', state.entry?.email ?? '—'],
    [
      isSubscription ? 'Total entries / month' : 'Total entries',
      `${Number(state.totalEntries ?? 0).toLocaleString('en-AU')} entries`
    ],
    [
      isSubscription
        ? 'Digital Collectible rewards / month'
        : 'Digital Collectible rewards',
      `${Number(state.nftRewards ?? 0)} Digital Collectibles`
    ],
    [
      isSubscription ? 'INC earned / month' : 'INC earned',
      `A$${Number(state.incPerMonth ?? 0)} of INC`
    ],
    [
      isSubscription ? 'Amount paid / month' : 'Amount paid',
      `A$${Number(state.amountPaidAud ?? 0).toFixed(2)}`
    ]
  ];

  return shell(
    <Box sx={{ flex: 1 }}>
      {/* ── the headline ─────────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 8 },
          pb: { xs: 4, md: 5 },
          textAlign: 'center'
        }}
      >
        <Typography sx={{ ...kickerSx, fontSize: { xs: 11, md: 12 }, mb: 1.75 }}>
          YOU&apos;RE SUBSCRIBED
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 32, md: 48 },
            fontWeight: 900,
            lineHeight: 1.08,
            color: INK,
            mb: 1.5
          }}
        >
          Congratulations
        </Typography>
        <Typography sx={{ fontSize: { xs: 17, md: 20 }, fontWeight: 800, color: CYAN }}>
          {state.selectedPack?.title
            ? `${state.selectedPack.title} is active — you're in every draw`
            : "You're officially in the draw"}
        </Typography>

        {/* Where the draw is watched (Facebook) and the community is (Discord),
            the same row the confirmation page shows under its headline. */}
        <Box sx={{ mt: { xs: 3, md: 3.5 } }}>
          <GiveawayOfferSocialLinks page="subscribed" />
        </Box>
      </Box>

      {/* ── what they bought ─────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ ...panelSx, p: { xs: 2.5, md: 3.5 } }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: '0.16em',
              color: MUTED,
              textAlign: 'center',
              mb: 2.5
            }}
          >
            ENTRY DETAILS
          </Typography>

          {rows.map(([label, value], i) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.6,
                borderTop: i === 0 ? 'none' : `1px solid ${LINE}`
              }}
            >
              <Typography sx={{ fontSize: 14.5, color: BODY, fontWeight: 600 }}>
                {label}
              </Typography>
              {/* The value is the member's own text — an email can be longer than a
                  phone is wide. As a flex item it refused to shrink below that
                  width and pushed the whole page into a sideways scroll on narrow
                  phones. So the row may wrap (a long pair stacks, the value staying
                  on the right), the value may shrink (minWidth 0) and an unbroken
                  address breaks where it must (overflowWrap). */}
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 900,
                  color: i === rows.length - 1 ? GOLD : INK,
                  textAlign: 'right',
                  ml: 'auto',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflowWrap: 'anywhere'
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── the password card: what replaces "go to member portal" ───── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2.5, md: 4 }, mt: { xs: 2.5, md: 3 } }}>
        <Box
          sx={{
            ...panelSx,
            p: { xs: 2.5, md: 3.5 },
            textAlign: 'center',
            borderColor: 'rgba(56,232,255,0.35)'
          }}
        >
          <Box
            sx={{
              mx: 'auto',
              mb: 1.75,
              width: 52,
              height: 52,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(56,232,255,0.12)',
              border: '1px solid rgba(56,232,255,0.35)'
            }}
          >
            <EmailOutlinedIcon sx={{ fontSize: 26, color: CYAN }} />
          </Box>

          <Typography
            component="h2"
            sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 900, color: INK, mb: 1 }}
          >
            Check your email to create your password
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 14.5, md: 15.5 },
              color: BODY,
              lineHeight: 1.65,
              maxWidth: 560,
              mx: 'auto'
            }}
          >
            When you signed up we sent you an email with a link to set your
            password. That link is how you get into your account to see your
            entries and manage your subscription.
          </Typography>

          <Typography sx={{ mt: 1.75, fontSize: 13.5, color: MUTED, lineHeight: 1.6 }}>
            Cannot find it? Check your spam folder, or email{' '}
            <Box
              component="a"
              href="mailto:support@incrypt.com.au"
              sx={{ color: CYAN, textDecoration: 'none', fontWeight: 700 }}
            >
              support@incrypt.com.au
            </Box>{' '}
            and we will resend it.
          </Typography>
        </Box>
      </Box>

      {/* ── discord ──────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2.5, md: 4 }, mt: { xs: 2.5, md: 3 } }}>
        <Box sx={{ ...panelSx, p: { xs: 2.5, md: 3.5 }, textAlign: 'center' }}>
          <Typography
            component="h2"
            sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 900, color: INK, mb: 1 }}
          >
            Join our Discord community
          </Typography>
          {/* About the community only. The draws are watched on Facebook, and
              this sentence used to send people here to watch them. */}
          <Typography sx={{ fontSize: 14.5, color: BODY, mb: 2.5, lineHeight: 1.6 }}>
            Get involved in the INCRYPT community and meet other members.
          </Typography>
          <Button
            component="a"
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              px: 3.5,
              py: 1.15,
              borderRadius: 999,
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              color: INK,
              border: `1px solid ${LINE}`,
              background: 'rgba(255,255,255,0.04)',
              '&:hover': { background: 'rgba(56,232,255,0.10)', borderColor: CYAN }
            }}
          >
            Join Discord server
          </Button>
        </Box>
      </Box>

      {/* ── what happens next ────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 1040,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 7 },
          pb: { xs: 5, md: 8 }
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: 22, md: 28 },
            fontWeight: 900,
            color: INK,
            textAlign: 'center',
            mb: { xs: 3, md: 4 }
          }}
        >
          What happens next?
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 2, md: 2.5 }
          }}
        >
          {nextSteps.map(({ Icon, title, copy }, i) => (
            <Box key={title} sx={{ ...panelSx, p: 2.5, textAlign: 'left' }}>
              <Icon sx={{ fontSize: 24, color: i === 0 ? CYAN : MUTED, mb: 1.25 }} />
              <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: INK, mb: 0.75 }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: 14, color: BODY, lineHeight: 1.6 }}>
                {copy}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 5 } }}>
          <Button onClick={() => navigate('/giveaway')} sx={ctaSx}>
            Back to the giveaway
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default GiveawayOfferSubscribedPage;
